import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Scales, CheckCircle } from "@phosphor-icons/react";

export default function SetPassword() {
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [validating, setValidating] = useState(true);
  const [tokenState, setTokenState] = useState({ ok: false, email: "", error: "" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setTokenState({ ok: false, email: "", error: "Missing invitation token." });
      setValidating(false);
      return;
    }
    axios
      .get(`${API}/auth/guest/validate-token`, { params: { token, kind: "invite" } })
      .then((r) => setTokenState({ ok: true, email: r.data.email, error: "" }))
      .catch((e) => setTokenState({ ok: false, email: "", error: e?.response?.data?.detail || "Invalid link." }))
      .finally(() => setValidating(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (password !== confirm) return toast.error("Passwords do not match.");
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/guest/set-password`, { token, password });
      setDone(true);
      toast.success("Password set. You can now sign in.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not set your password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-10 w-10 rounded-sm bg-slate-900 flex items-center justify-center">
            <Scales size={22} weight="bold" className="text-white" />
          </div>
          <div className="font-display text-xl text-slate-900">{branding.firm_name}</div>
        </div>

        {validating ? (
          <p data-testid="setpwd-validating" className="text-sm text-slate-500">Checking invitation…</p>
        ) : done ? (
          <div data-testid="setpwd-done">
            <div className="flex items-center gap-2 text-emerald-700 mb-3">
              <CheckCircle size={22} weight="fill" />
              <div className="font-display text-2xl text-slate-900">Password set</div>
            </div>
            <p className="text-sm text-slate-500 mb-6">You're all set. Sign in with your email and new password.</p>
            <button data-testid="setpwd-goto-login" onClick={() => navigate("/login")} className="w-full px-4 py-3 rounded-sm bg-slate-900 text-white text-sm font-medium hover:bg-slate-800">
              Continue to sign in
            </button>
          </div>
        ) : !tokenState.ok ? (
          <div data-testid="setpwd-invalid">
            <div className="label-tracking mb-2">Invitation</div>
            <h2 className="font-display text-3xl text-slate-900 mb-4">Link expired</h2>
            <p className="text-sm text-slate-500 mb-6">{tokenState.error}</p>
            <Link to="/login" className="text-sm text-blue-600 hover:underline">Back to sign in</Link>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="setpwd-form">
            <div className="label-tracking">Welcome</div>
            <h2 className="font-display text-3xl sm:text-4xl text-slate-900 mt-2 leading-tight">Set your password.</h2>
            <p className="text-sm text-slate-500 mt-3 mb-8">Account: <span className="font-mono text-slate-900">{tokenState.email}</span></p>
            <label className="block text-xs font-medium text-slate-600 mb-1">New password (min 8 chars)</label>
            <input
              data-testid="setpwd-password"
              type="password" autoComplete="new-password" value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full mb-4 px-3 py-2.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:border-slate-900"
              required minLength={8}
            />
            <label className="block text-xs font-medium text-slate-600 mb-1">Confirm password</label>
            <input
              data-testid="setpwd-confirm"
              type="password" autoComplete="new-password" value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full mb-6 px-3 py-2.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:border-slate-900"
              required minLength={8}
            />
            <button
              data-testid="setpwd-submit"
              type="submit" disabled={submitting}
              className="w-full px-4 py-3 rounded-sm bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-60"
            >
              {submitting ? "Setting password…" : "Set password"}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
